/** The pieces Conversations is drawn from, as the approved prototype draws them: rows, lines, bubbles, cards. */
import { type HTMLAttributes, type ReactNode, useState } from "react";
import type { Attachment } from "../../shared/types";
import type { HostDirectRow, TalkAnnouncement, TalkFace, TalkFile, TalkGroup, TalkGroupSets, TalkMessage, TalkPoll, TalkWho } from "../../shared/talk";
import { Button } from "../components/ui/Button";
import { Face, FaceStack } from "../components/ui/Face";
import { Icon } from "../components/ui/Icon";
import { PrivateSendSheet, startMessage } from "../views/rooms/PrivateSendSheet";
import { groupWords } from "../views/rooms/roomWords";
import { AttCard, srcOf } from "./attachments";
import { useHost } from "./useHost";
import { dateWords, firstName, lineWhen, postWhen } from "./when";

export const faceOf = (w: { name: string; photoUrl: string | null }) => ({ name: w.name, photo: w.photoUrl });
export const facesOf = (list: TalkFace[]) => list.map(faceOf);

/** A room's picture, or a quiet square when it has none. */
export function Poster({ url, className }: { url: string | null; className?: string }) {
  return url ? (
    <img className={className} src={url} alt="" />
  ) : (
    <span className="sp-ph">
      <Icon name="play" />
    </span>
  );
}

/** A person row in the Direct rail: face, name, last words, when. */
export function PersonRow({ row, line, when, on, onClick }: { row: HostDirectRow; line: string; when: string; on?: boolean; onClick?: () => void }) {
  return (
    <button type="button" className={on ? "pr on" : "pr"} onClick={onClick}>
      <Face name={row.name} photo={row.photoUrl} size={36} />
      <span className="pr-t">
        <b>{row.name}</b>
        <span>{line}</span>
      </span>
      <span className="pr-r">
        <small>{when}</small>
        {row.unread ? <i className="pr-dot" /> : null}
      </span>
    </button>
  );
}

/** A message bubble in Direct: from the host on the right, from the person on the left. */
export function Bubble({
  who,
  mine,
  children,
  when,
  note,
  hearted,
  actions,
  hold,
}: {
  who: TalkWho;
  mine?: boolean;
  children: ReactNode;
  when?: string;
  note?: ReactNode;
  /** The host's heart on this line. */
  hearted?: boolean;
  /** The hover toolbar on a computer: a heart, the dots. */
  actions?: ReactNode;
  /** A phone's press and hold, which opens the line's actions. */
  hold?: HTMLAttributes<HTMLDivElement>;
}) {
  return (
    <div className={mine ? "bb mine" : "bb"} {...hold}>
      {mine ? null : <Face name={who.name} photo={who.photoUrl} size={30} />}
      <div className="bb-s">
        {note ? <span className="bb-note">{note}</span> : null}
        {actions ? <span className="bb-acts">{actions}</span> : null}
        <div className="bb-b">
          {children}
          {hearted ? (
            <span className="bb-heart" aria-label="You hearted this">
              <Icon name="heart" fill />
            </span>
          ) : null}
        </div>
        {when ? <small className="bb-w">{when}</small> : null}
      </div>
    </div>
  );
}

/** The word a search found, lit wherever the line holds it. */
export function Marked({ text, word }: { text: string; word: string }) {
  if (!word) return <>{text}</>;
  const lower = text.toLowerCase();
  const find = word.toLowerCase();
  const out: ReactNode[] = [];
  let from = 0;
  for (let at = lower.indexOf(find); at >= 0; at = lower.indexOf(find, from)) {
    out.push(text.slice(from, at), <mark key={at}>{text.slice(at, at + find.length)}</mark>);
    from = at + find.length;
  }
  out.push(text.slice(from));
  return <>{out}</>;
}

/** A file's face in a list: paper for a document, a frame for a replay, a picture. */
export function FileTile({ f }: { f: TalkFile }) {
  if (f.kind === "video")
    return (
      <span className="ft vid">
        {f.posterUrl ? <img src={f.posterUrl} alt="" /> : null}
        <Icon name="playf" />
      </span>
    );
  if (f.kind === "picture" || f.kind === "link")
    return (
      <span className="ft pic">
        <Icon name={f.kind === "link" ? "link" : "pic"} />
      </span>
    );
  return (
    <span className="ft pdf">
      <b>{(f.name.split(".").pop() ?? "").slice(0, 4).toUpperCase() || "FILE"}</b>
    </span>
  );
}

/** A first page, drawn small, for the preview of a document. */
export function DocPage({ name }: { name: string }) {
  return (
    <div className="doc" aria-hidden="true">
      <b>{name.replace(/\.[^.]+$/, "")}</b>
      <i style={{ width: "88%" }} />
      <i style={{ width: "74%" }} />
      <i style={{ width: "81%" }} />
      <i style={{ width: "66%" }} />
      <i style={{ width: "78%" }} />
      <i style={{ width: "58%" }} />
    </div>
  );
}

/** What was shared with a post, as small chips that open the file. */
function FileChips({ list }: { list: Attachment[] }) {
  return (
    <>
      {list.map((a, i) => {
        const src = srcOf(a);
        const inner = (
          <>
            <Icon name="file" /> {a.name}
          </>
        );
        return src ? (
          <a key={`${a.name}-${i}`} className="post-file" href={src} target="_blank" rel="noreferrer noopener">
            {inner}
          </a>
        ) : (
          <span key={`${a.name}-${i}`} className="post-file">
            {inner}
          </span>
        );
      })}
    </>
  );
}

/** A post in a room's Announcements: Maya sees who saw it, who hearted it and who replied. */
export function PostCard({ p, host, onReplies }: { p: TalkAnnouncement; host: { name: string; photo: string | null }; onReplies: () => void }) {
  const replied = p.repliedBy ?? [];
  return (
    <article className="post" data-line={p.id}>
      <header className="post-h">
        <Face name={host.name} photo={host.photo} size={36} />
        <span className="post-who">
          <b>{host.name}</b>
          <small>
            {postWhen(p.createdAt)}
            {p.auto ? ` · posted for you when ${p.auto}` : ""}
          </small>
        </span>
        {p.pinned ? (
          <span className="post-tag">
            <Icon name="pin" /> Pinned
          </span>
        ) : null}
        {p.emailed ? (
          <span className="post-tag">
            <Icon name="mail" /> Emailed
          </span>
        ) : null}
      </header>
      <p className="post-t">{p.body}</p>
      {p.replay ? (
        <span className="post-replay">
          <span className="post-replay-pic">
            {p.replay.posterUrl ? <img src={p.replay.posterUrl} alt="" /> : null}
            <Icon name="playf" />
          </span>
          <span className="post-replay-t">
            <small>The replay</small>
            <b>{p.replay.title}</b>
            <span>
              {dateWords(p.replay.startsAt)} · {p.replay.minutes} min
            </span>
          </span>
        </span>
      ) : null}
      <FileChips list={p.attachments} />
      <footer className="post-f">
        <span className="post-seen">
          <Icon name="eye" /> Seen by {(p.seen ?? 0).toLocaleString("en-US")}
        </span>
        <span className="react">
          <Icon name="heart" /> {p.hearts}
        </span>
        {replied.length ? (
          <button type="button" className="post-rep" onClick={onReplies}>
            <FaceStack people={facesOf(replied.slice(0, 3))} size={22} />
            {p.replied ?? replied.length} replied to you
            <Icon name="chev" />
          </button>
        ) : null}
      </footer>
    </article>
  );
}

/** A room's groups as rows: faces, the name, how many. A row opens the group's sheet. */
export function GroupRows({ groups, onOpen }: { groups: TalkGroup[]; onOpen: (g: TalkGroup) => void }) {
  return (
    <>
      {groups.map((g) => (
        <button key={g.key} type="button" className="mem tgrp tap" onClick={() => onOpen(g)}>
          <FaceStack people={facesOf(g.faces.slice(0, 3))} size={22} />
          <span className="mem-t">
            <b>{g.name}</b>
          </span>
          {g.now ? <i className="grp-now" aria-label="Right now" /> : null}
          <span className="mem-s">{g.count.toLocaleString("en-US")}</span>
        </button>
      ))}
    </>
  );
}

/** A weekly room reads two ways: one session (the last by default), or across all its sessions. */
export function SessionSwitch({ sets, all, onAll }: { sets: TalkGroupSets[]; all: boolean; onAll: (all: boolean) => void }) {
  const last = sets[0]?.session;
  return (
    <div className="gseg" role="tablist" aria-label="Which groups">
      <button type="button" role="tab" aria-selected={!all} className={all ? undefined : "on"} onClick={() => onAll(false)}>
        {last ? `Last live · ${dateWords(last.startsAt)}` : "Last live"}
      </button>
      <button type="button" role="tab" aria-selected={all} className={all ? "on" : undefined} onClick={() => onAll(true)}>
        All sessions
      </button>
    </div>
  );
}

/** What All sessions says while its groups are all empty: Regulars and Dropped off need a second live to mean anything. */
export function AfterSecondLive() {
  return <p className="empty-note">Regulars and who dropped off show after the second live.</p>;
}

/** The set of groups a switch picks, and what to list in it: a group nobody is in stays out. */
export function pickedSet(sets: TalkGroupSets[], all: boolean): TalkGroupSets | undefined {
  return sets.length > 1 && all ? sets[1] : sets[0];
}

/** A room's groups, with the switch when the room is weekly. A group opens the same sheet the Audience screen opens. */
export function RoomGroups({ sets, all, onAll, room, onSent }: { sets: TalkGroupSets[]; all: boolean; onAll: (all: boolean) => void; room: { id: string; title: string }; onSent: () => void }) {
  const [open, setOpen] = useState<TalkGroup | null>(null);
  const hostFirst = firstName(useHost().name);
  const set = pickedSet(sets, all);
  const shown = (set?.groups ?? []).filter((g) => g.count > 0);
  const words = open && set ? groupWords(open.key, set.scope, { title: room.title, live: undefined }, Date.now()) : null;
  const act = words && open ? words.act(open.count) : "";
  return (
    <>
      {sets.length > 1 ? <SessionSwitch sets={sets} all={all} onAll={onAll} /> : null}
      <GroupRows groups={shown} onOpen={setOpen} />
      {shown.length === 0 && set?.scope === "series" ? <AfterSecondLive /> : null}
      {open && set && words ? (
        <PrivateSendSheet
          key={open.key}
          send={{
            seed: { webinarId: room.id, scope: set.scope, key: open.key, ...(set.session ? { session: set.session.id } : {}) },
            title: act || words.label,
            ...(act ? {} : { circleOnly: true }),
            reach: open.count,
            faces: open.faces.map((f) => ({ name: f.name, avatar_url: f.photoUrl })),
            label: set.session ? `${words.label}, ${dateWords(set.session.startsAt)}` : words.label,
            subject: words.subject,
            message: startMessage(words.message, hostFirst),
          }}
          onClose={() => setOpen(null)}
          onSent={onSent}
        />
      ) : null}
    </>
  );
}

/** An email, or an older update, in a one to one thread: it keeps its subject, and shows how it went. */
export function MailCard({ label = "Email", subject, children, when, files, replies }: { label?: string; subject: string | null; children: ReactNode; when: string; files?: Attachment[]; replies?: ReactNode }) {
  return (
    <div className="mailc">
      <span className="mailc-h">
        <Icon name="mail" /> {label}
        {subject ? ` · ${subject}` : ""}
      </span>
      <p>{children}</p>
      {files?.length ? <FileChips list={files} /> : null}
      {replies}
      <small>{when}</small>
    </div>
  );
}

/** A line in a room or circle chat. */
export function Line({
  m,
  size = 38,
  host,
  onHeart,
  onReply,
  onMenu,
  menu,
  menuOpen,
  onTap,
}: {
  m: TalkMessage;
  size?: number;
  host: { name: string; photo: string | null };
  onHeart?: () => void;
  onReply?: () => void;
  onMenu?: () => void;
  menu?: ReactNode;
  menuOpen?: boolean;
  /** A phone has no hover: pressing a line opens its actions. */
  onTap?: () => void;
}) {
  const cls = ["ln", menuOpen ? "sel" : "", m.hidden ? "hid" : ""].filter(Boolean).join(" ");
  return (
    <div className={cls} data-line={m.id} onClick={onTap}>
      <Face name={m.who.name} photo={m.who.photoUrl} size={size} />
      <div className="ln-b">
        <b>
          {m.who.name}
          {m.who.kind === "host" ? <span className="host">Host</span> : null}
          <small>{lineWhen(m.createdAt)}</small>
        </b>
        {m.body ? <p className="ln-p">{m.body}</p> : null}
        {m.attachments.length ? (
          <span className="ln-att">
            {m.attachments.map((a, i) => (
              <AttCard key={`${a.name}-${i}`} a={a} host={host} />
            ))}
          </span>
        ) : null}
        {m.poll ? <Poll poll={m.poll} /> : null}
        {m.hearts || m.replies ? (
          <span className="ln-f">
            {m.hearts ? (
              <span className="react">
                <Icon name="heart" /> {m.hearts}
              </span>
            ) : null}
            {m.replies ? <span className="ln-r">{m.replies === 1 ? "1 reply" : `${m.replies} replies`}</span> : null}
          </span>
        ) : null}
      </div>
      {onHeart || onReply || onMenu ? (
        <span className="ln-acts">
          {onHeart ? (
            <button type="button" aria-label="Heart" aria-pressed={m.hearted} onClick={onHeart}>
              <Icon name="heart" fill={m.hearted} />
            </button>
          ) : null}
          {onReply ? (
            <button type="button" aria-label="Reply" onClick={onReply}>
              <Icon name="chat" />
            </button>
          ) : null}
          {onMenu ? (
            <button type="button" aria-label="More" className={menuOpen ? "on" : undefined} onClick={onMenu}>
              <Icon name="more" />
            </button>
          ) : null}
        </span>
      ) : null}
      {menu}
    </div>
  );
}

/** A question with choices: how each answer is doing, and how many voted. */
export function Poll({ poll }: { poll: TalkPoll }) {
  const top = Math.max(0, ...poll.options.map((o) => o.votes));
  return (
    <div className="poll">
      <p>{poll.question}</p>
      {poll.options.map((o) => (
        <span key={o.label} className={top > 0 && o.votes === top ? "poll-o on" : "poll-o"}>
          <i style={{ width: `${poll.voted ? Math.round((o.votes / poll.voted) * 100) : 0}%` }} />
          <b>{o.label}</b>
          <em>{o.votes}</em>
        </span>
      ))}
      <small>
        {poll.voted} of {poll.of} voted
      </small>
    </div>
  );
}

/** A circle call's replay, posted into the circle. */
export function CallCard({ m, members, onWatch }: { m: TalkMessage; members: number; onWatch: () => void }) {
  const c = m.call;
  if (!c) return null;
  return (
    <div className="callcard">
      <span className="cc-pic">
        {c.faces.slice(0, 4).map((f, i) => (f.photoUrl ? <img key={`${f.name}-${i}`} src={f.photoUrl} alt="" /> : <span key={`${f.name}-${i}`} />))}
        <Icon name="playf" />
      </span>
      <span className="cc-t">
        <small>The call's replay</small>
        <b>{c.title}</b>
        <span>
          {c.minutes} min · {c.came} came · here for all {members}
        </span>
      </span>
      <Button size="s" icon="play" onClick={onWatch}>
        Watch
      </Button>
    </div>
  );
}

/** The header of a room, a circle or a person: a picture, a title, one line under it, one thing on the right. */
export function Head({ pic, title, sub, right, onInfo, infoLabel }: { pic?: ReactNode; title: string; sub: string; right?: ReactNode; onInfo?: () => void; infoLabel?: string }) {
  return (
    <div className="mh">
      {pic ?? (
        <span className="mh-circle">
          <Icon name="target" />
        </span>
      )}
      <div className="mh-t">
        <h2>{title}</h2>
        {onInfo ? (
          <button type="button" className="mh-sub" aria-label={infoLabel} onClick={onInfo}>
            {sub}
            <Icon name="info" />
          </button>
        ) : (
          <p>{sub}</p>
        )}
      </div>
      {right}
    </div>
  );
}
